// ---------------------------------------------------------------------------
// Marketplace Collaborations Schema (MKT-016 Phase 1)
// ---------------------------------------------------------------------------
// This is a revenue-partner marketplace flow, not delegated tenant access.
// Do not collapse it into organization collaboration/access-grant surfaces.

import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { organization, user } from "./auth";

const tz = { withTimezone: true } as const;

export const MARKETPLACE_COLLABORATION_REQUEST_STATUSES = [
  "draft",
  "pending",
  "accepted",
  "rejected",
  "cancelled",
  "expired",
] as const;
export type MarketplaceCollaborationRequestStatus =
  (typeof MARKETPLACE_COLLABORATION_REQUEST_STATUSES)[number];

export interface MarketplaceCollaborationScope {
  verticals?: string[];
  pipelines?: string[];
  clientAllowlist?: string[];
  clientBlocklist?: string[];
  expertiseTags?: string[];
  notes?: string;
  [key: string]: unknown;
}

export const marketplaceCollaborationRequests = pgTable(
  "marketplace_collaboration_requests",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    inviterOrgId: text("inviter_org_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    inviteeOrgId: text("invitee_org_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("draft"),
    scope: jsonb("scope").$type<MarketplaceCollaborationScope>().notNull(),
    proposedSplitBasisPoints: integer("proposed_split_basis_points").notNull(),
    acceptedSplitBasisPoints: integer("accepted_split_basis_points"),
    expiresAt: timestamp("expires_at", tz)
      .notNull()
      .default(sql`now() + interval '14 days'`),
    createdBy: text("created_by")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    acceptedBy: text("accepted_by").references(() => user.id, { onDelete: "restrict" }),
    rejectedBy: text("rejected_by").references(() => user.id, { onDelete: "restrict" }),
    cancelledBy: text("cancelled_by").references(() => user.id, { onDelete: "restrict" }),
    acceptedAt: timestamp("accepted_at", tz),
    rejectedAt: timestamp("rejected_at", tz),
    cancelledAt: timestamp("cancelled_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_mkt_collab_req_inviter").on(table.inviterOrgId, table.status),
    index("idx_mkt_collab_req_invitee").on(table.inviteeOrgId, table.status),
    index("idx_mkt_collab_req_expires").on(table.status, table.expiresAt),
    uniqueIndex("uq_mkt_collab_req_active_pair")
      .on(
        sql`LEAST(${table.inviterOrgId}, ${table.inviteeOrgId})`,
        sql`GREATEST(${table.inviterOrgId}, ${table.inviteeOrgId})`,
      )
      .where(sql`${table.status} IN ('pending', 'accepted')`),
    check(
      "chk_mkt_collab_req_status",
      sql`${table.status} IN ('draft', 'pending', 'accepted', 'rejected', 'cancelled', 'expired')`,
    ),
    check(
      "chk_mkt_collab_req_not_self",
      sql`${table.inviterOrgId} <> ${table.inviteeOrgId}`,
    ),
    check(
      "chk_mkt_collab_req_proposed_split_range",
      sql`${table.proposedSplitBasisPoints} BETWEEN 0 AND 10000`,
    ),
    check(
      "chk_mkt_collab_req_accepted_split_range",
      sql`${table.acceptedSplitBasisPoints} IS NULL OR ${table.acceptedSplitBasisPoints} BETWEEN 0 AND 10000`,
    ),
  ],
);

export const marketplaceCollaborationAttributionRows = pgTable(
  "marketplace_collaboration_attribution_rows",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    collaborationId: uuid("collaboration_id")
      .notNull()
      .references(() => marketplaceCollaborationRequests.id, { onDelete: "cascade" }),
    dealId: uuid("deal_id").notNull(),
    tenantId: uuid("tenant_id").notNull(),
    inviterRevenueCents: integer("inviter_revenue_cents").notNull(),
    inviteeRevenueCents: integer("invitee_revenue_cents").notNull(),
    currency: text("currency").notNull().default("EUR"),
    recordedAt: timestamp("recorded_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_mkt_collab_attr_collaboration").on(table.collaborationId),
    index("idx_mkt_collab_attr_tenant").on(table.tenantId, table.recordedAt),
    uniqueIndex("uq_mkt_collab_attr_deal").on(table.collaborationId, table.dealId),
    check(
      "chk_mkt_collab_attr_inviter_revenue_non_negative",
      sql`${table.inviterRevenueCents} >= 0`,
    ),
    check(
      "chk_mkt_collab_attr_invitee_revenue_non_negative",
      sql`${table.inviteeRevenueCents} >= 0`,
    ),
    check("chk_mkt_collab_attr_currency", sql`length(${table.currency}) = 3`),
  ],
);
